import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../contexts/UserContext'; // Importar el hook del contexto de usuario
import { useMessage } from '../contexts/MessageContext'; // Importar el hook de mensajes
import { handleApiError } from '../config/apiConfig'; // Importar la función de manejo de errores
import GoogleLoginButton from './GoogleLogin';
import FacebookLoginButton from './FacebookLogin';

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  const [loading, setLoading] = useState(false); // Estado para manejar la carga
  const [isLeaving, setIsLeaving] = useState(false);
  const leaveTimeoutRef = useRef(null);

  const navigate = useNavigate(); // Hook para la navegación
  const { login } = useUser(); // Obtener la función de login del contexto
  const { showMessage, clearMessages } = useMessage(); // Obtener la función de mensajes y limpiar mensajes

  const navigateWithExit = (to) => {
    setIsLeaving(true);
    leaveTimeoutRef.current = window.setTimeout(() => {
      navigate(to);
    }, 220);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación básica de entrada
    if (!credentials.username.trim() || !credentials.password.trim()) {
      showMessage('Por favor ingrese usuario y contraseña', 'error');
      return;
    }

    setLoading(true); // Activar indicador de carga

    try {
      const result = await login(credentials);
      if (result.success) {
        clearMessages(); // Limpiar mensajes anteriores
        showMessage('Inicio de sesión exitoso', 'success');
        navigateWithExit('/admin');
      } else {
        showMessage(result.message || 'Credenciales incorrectas', 'error');
      }
    } catch (error) {
      console.error('Error en el login:', error);
      const errorMessage = handleApiError(error);
      showMessage(errorMessage || 'Error en el login', 'error');
    } finally {
      setLoading(false);
    }
 };

  const handleGoogleSuccess = (user) => {
    console.log('Inicio de sesión exitoso con Google:', user);
    // Aquí puedes manejar la lógica después del inicio de sesión con Google
    // Usar el contexto de usuario para mantener la sesión
    navigateWithExit('/admin'); // redirigir siempre al panel admin
  };

  const handleGoogleError = (error) => {
    console.error('Error en el inicio de sesión con Google:', error);
    showMessage('Error en el inicio de sesión con Google: ' + error, 'error');
  };

  const handleFacebookSuccess = (user) => {
    console.log('Inicio de sesión exitoso con Facebook:', user);
    // Aquí puedes manejar la lógica después del inicio de sesión con Facebook
    // Usar el contexto de usuario para mantener la sesión
    navigateWithExit('/admin'); // redirigir siempre al panel admin
  };

  const handleFacebookError = (error) => {
    console.error('Error en el inicio de sesión con Facebook:', error);
    showMessage('Error en el inicio de sesión con Facebook: ' + error, 'error');
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');

    const toggleNoScroll = () => {
      if (mediaQuery.matches) {
        document.documentElement.classList.add('auth-no-scroll-mobile');
        document.body.classList.add('auth-no-scroll-mobile');
      } else {
        document.documentElement.classList.remove('auth-no-scroll-mobile');
        document.body.classList.remove('auth-no-scroll-mobile');
      }
    };

    toggleNoScroll();
    mediaQuery.addEventListener('change', toggleNoScroll);

    return () => {
      mediaQuery.removeEventListener('change', toggleNoScroll);
      document.documentElement.classList.remove('auth-no-scroll-mobile');
      document.body.classList.remove('auth-no-scroll-mobile');
    };
  }, []);

  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) {
        window.clearTimeout(leaveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <motion.section
      initial={{ opacity: 0, y: 20, scale: 0.985 }}
      animate={isLeaving ? { opacity: 0, y: -16, scale: 0.985 } : { opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex h-[calc(100dvh-6rem)] items-center overflow-hidden px-3 py-3 sm:h-auto sm:min-h-[calc(100vh-4rem)] sm:px-6 sm:py-10"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-blue-300/40 blur-3xl dark:bg-blue-900/30" />
        <div className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-900/30" />
      </div>

      <div className="relative mx-auto w-full max-w-xl max-h-full">
        <div className="rounded-3xl border border-slate-200/70 bg-white/85 p-4 shadow-2xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/85 sm:p-7 lg:p-8">
          <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 sm:text-3xl">Iniciar sesión</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 sm:mt-2">Accede con tu usuario o correo para entrar al panel.</p>

          <form onSubmit={handleSubmit} className="mt-4 space-y-3 sm:mt-6 sm:space-y-4">
            <div>
              <label htmlFor="login-username" className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200 sm:mb-2">Usuario o email</label>
              <input
                id="login-username"
                type="text"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-slate-900 shadow-sm outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-900/40 sm:py-3"
                autoComplete="username"
                placeholder="usuario@email.com"
              />
            </div>

            <div>
              <label htmlFor="login-password" className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200 sm:mb-2">Contraseña</label>
              <input
                id="login-password"
                type="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-slate-900 shadow-sm outline-none transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-900/40 sm:py-3"
                autoComplete="current-password"
                placeholder="Tu contraseña"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full rounded-xl py-2.5 px-4 text-sm font-bold uppercase tracking-wide text-white transition-all duration-300 sm:py-3 sm:text-base ${
                loading
                  ? 'cursor-not-allowed bg-slate-400 dark:bg-slate-600'
                  : 'bg-blue-600 shadow-lg shadow-blue-300/40 hover:-translate-y-0.5 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
              }`}
            >
              {loading ? 'Iniciando sesión...' : 'Entrar'}
            </button>
          </form>

          <p className="mt-4 text-center text-sm text-slate-600 dark:text-slate-300 sm:mt-6">
            ¿No tienes cuenta?{' '}
            <Link
              to="/register"
              onClick={(e) => {
                e.preventDefault();
                navigateWithExit('/register');
              }}
              className="font-bold text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Regístrate aquí
            </Link>
          </p>
        </div>
      </div>
    </motion.section>
  );
};

export default Login;
